import * as THREE from "three";
import { MeshBVH, acceleratedRaycast, disposeBoundsTree } from "three-mesh-bvh";

export const computeVolume = (geometry) => {
  const pos = geometry.attributes.position;
  if (!pos) return 0;
  const indices = geometry.index?.array;
  const count = indices ? indices.length : pos.count;
  const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3();
  let volume = 0;
  for (let i = 0; i < count; i += 3) {
    a.fromBufferAttribute(pos, indices ? indices[i] : i);
    b.fromBufferAttribute(pos, indices ? indices[i + 1] : i + 1);
    c.fromBufferAttribute(pos, indices ? indices[i + 2] : i + 2);
    volume += a.dot(b.cross(c)) / 6;
  }
  return Math.abs(volume);
};

const sampleGrid = (size) => {
  let nx = Math.max(20, Math.ceil(size.x / 1.5));
  let nz = Math.max(20, Math.ceil(size.z / 1.5));
  if (nx * nz > 12000) {
    const shrink = Math.sqrt(12000 / (nx * nz));
    nx = Math.max(20, Math.floor(nx * shrink));
    nz = Math.max(20, Math.floor(nz * shrink));
  }
  return { nx, nz, dx: size.x / nx, dz: size.z / nz };
};

const sampleColumns = (mesh, bbox, size, grid) => {
  const raycaster = new THREE.Raycaster();
  raycaster.far = size.y * 1.5 + 10;
  raycaster.firstHitOnly = false;
  const origin = new THREE.Vector3(), direction = new THREE.Vector3(0, 1, 0);
  const y0 = bbox.min.y - Math.max(1, size.y * 0.001);
  let length = 0;
  for (let x = 0; x < grid.nx; x++) {
    for (let z = 0; z < grid.nz; z++) {
      origin.set(bbox.min.x + (x + 0.5) * grid.dx, y0, bbox.min.z + (z + 0.5) * grid.dz);
      raycaster.set(origin, direction);
      const hits = raycaster.intersectObject(mesh, false);
      // A ray on a shared triangle edge can hit the same surface twice.
      const distances = hits.map((hit) => hit.distance).filter((distance, i, all) => i === 0 || distance - all[i - 1] > 1e-6);
      for (let i = 0; i + 1 < distances.length; i += 2) length += distances[i + 1] - distances[i];
    }
  }
  return length * grid.dx * grid.dz;
};

export const computeSampledVolume = (geometry) => {
  if (!geometry.boundingBox) geometry.computeBoundingBox();
  const bbox = geometry.boundingBox;
  const size = bbox.getSize(new THREE.Vector3());
  if (size.x <= 0 || size.y <= 0 || size.z <= 0) return 0;
  const ownedBVH = !geometry.boundsTree;
  // Both entry and exit surfaces are needed for filled-column integration.
  const material = new THREE.MeshBasicMaterial({ side: THREE.DoubleSide });
  try {
    if (ownedBVH) geometry.boundsTree = new MeshBVH(geometry);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.raycast = acceleratedRaycast;
    mesh.updateMatrixWorld(true);
    return sampleColumns(mesh, bbox, size, sampleGrid(size));
  } finally {
    material.dispose();
    if (ownedBVH) disposeBoundsTree.call(geometry);
  }
};